import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { Kicker, Reveal, MaskedLines } from '../components/Reveal';
import { Md } from '../components/Markdown';
import { projectUrl } from '../data/projects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
const BRAND_LABEL = { media: 'F&L Media', souvenir: 'F&L Souvenir' };
const BRAND_COLORS = { media: '#14DA64', souvenir: '#0CBEDD' };
const SPANS = ['md:col-span-4', 'md:col-span-2', 'md:col-span-2', 'md:col-span-4', 'md:col-span-3', 'md:col-span-3'];
const RATIOS = ['aspect-[16/10]', 'aspect-[3/4]', 'aspect-[3/4]', 'aspect-[16/10]', 'aspect-[4/3]', 'aspect-[4/3]'];

const abs = (u) => (u && u.startsWith('/') ? `${BASE}${u}` : u);
const pick = (f, lang) => ((f?.[lang] || f?.pt || '').trim() ? f[lang] || f.pt : null);

function dbView(p, lang) {
  return {
    slug: p.slug,
    brand: p.brand,
    categories: p.categories || [],
    year: p.year || null,
    client: p.client || null,
    location: p.location || null,
    name: p.name?.[lang] || p.name?.pt || '',
    tagline: pick(p.tagline, lang),
    sections: {
      about: pick(p.intro, lang),
      objective: pick(p.objective, lang),
      challenge: pick(p.challenge, lang),
      whatWeDid: pick(p.what_we_did, lang),
      process: pick(p.process, lang),
      result: pick(p.result_text, lang),
    },
    services: p.services || [],
    metrics: p.metrics || [],
    media: (p.media || []).map((m) => ({
      media_type: m.media_type,
      full: abs(m.full),
      thumb: abs(m.thumb),
      poster: abs(m.poster),
      alt: m.alt,
    })),
  };
}

function Lightbox({ photos, index, onClose, onNav, closeLabel }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNav(1);
      if (e.key === 'ArrowLeft') onNav(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNav]);
  const ph = photos[index];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
      data-testid="project-lightbox"
      role="dialog"
      aria-modal="true"
    >
      <button onClick={onClose} aria-label={closeLabel} data-testid="lightbox-close" className="absolute right-5 top-5 text-zinc-400 transition-colors hover:text-white">
        <X className="h-7 w-7" />
      </button>
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onNav(-1); }} aria-label="Anterior" data-testid="lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors hover:text-white">
            <ChevronLeft className="h-10 w-10" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNav(1); }} aria-label="Seguinte" data-testid="lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors hover:text-white">
            <ChevronRight className="h-10 w-10" />
          </button>
        </>
      )}
      <img src={ph.full} alt="" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-full object-contain" data-testid="lightbox-image" />
    </motion.div>
  );
}

export default function ProjectPage() {
  const { lang, t } = useSite();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [dbProject, setDbProject] = useState(null);
  const [dbSummaries, setDbSummaries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let alive = true;
    setDbProject(null);
    setLoaded(false);
    const preview = searchParams.get('preview');
    Promise.all([
      fetch(`${API}/projects`).then((r) => (r.ok ? r.json() : { projects: [] })),
      fetch(`${API}/projects/${slug}${preview ? `?preview=${preview}` : ''}`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([list, detail]) => {
        if (!alive) return;
        setDbSummaries(list.projects || []);
        setDbProject(detail || null);
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    setLightbox(null);
  }, [slug]);

  const project = dbProject ? dbView(dbProject, lang) : null;

  const nextInfo = useMemo(() => {
    // A lista pública já vem ordenada pelo backend (destaques primeiro, depois ordem manual)
    const ordered = dbSummaries.map((p) => ({
      slug: p.slug,
      name: p.name?.[lang] || p.name?.pt || '',
      cover: abs(p.coverThumb || p.cover),
    }));
    const i = ordered.findIndex((s) => s.slug === slug);
    if (i < 0 || ordered.length < 2) return null;
    return ordered[(i + 1) % ordered.length];
  }, [dbSummaries, slug, lang]);

  useEffect(() => {
    if (!project) return;
    document.title = `${project.name} — ${t.meta.portfolio.title}`;
    const desc = document.querySelector('meta[name="description"]');
    const text = project.tagline || t.meta.portfolio.desc;
    if (desc) desc.setAttribute('content', text);
  }, [project, lang, t]);

  if (!project) {
    if (!loaded) return <div className="min-h-screen" data-testid="project-loading" />;
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-[1600px] flex-col items-start justify-center px-5 sm:px-8" data-testid="project-not-found">
        <p className="font-heading text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">{t.portfolio.notFound}</p>
        <Link to={`/${lang}/portfolio`} data-testid="project-back" className="mt-8 inline-flex items-center gap-2 border border-zinc-800 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:border-fl-yellow hover:text-fl-yellow">
          <ArrowLeft className="h-4 w-4" /> {t.project.back}
        </Link>
      </div>
    );
  }

  const color = BRAND_COLORS[project.brand] || '#FFCE00';
  const photos = project.media.filter((m) => m.media_type === 'photo');
  const heroVideo = project.media.find((m) => m.media_type === 'video');
  const { sections } = project;
  const hasWhat = sections.whatWeDid || sections.process || project.services.length > 0;
  const hasResult = sections.result || project.metrics.length > 0;
  const metaBits = [
    project.client ? `${t.project.client}: ${project.client}` : null,
    project.location,
    project.year,
  ].filter(Boolean);

  return (
    <div data-testid="project-page">
      {/* Abertura */}
      <section className="relative flex min-h-[70svh] items-end overflow-hidden">
        {heroVideo ? (
          <video autoPlay muted loop playsInline poster={heroVideo.poster || undefined} className="absolute inset-0 h-full w-full object-cover opacity-50" data-testid="project-hero-media">
            <source src={heroVideo.full} type="video/mp4" />
          </video>
        ) : (
          <img src={photos[0]?.full} alt={project.name} className="absolute inset-0 h-full w-full object-cover opacity-50" data-testid="project-hero-media" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-14 sm:px-8 sm:pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.7 }}>
            <p className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color }}>
              {BRAND_LABEL[project.brand] || 'F&L Group'}
              {metaBits.length > 0 && <span className="text-zinc-500 normal-case tracking-[0.15em]">· {metaBits.join(' · ')}</span>}
            </p>
          </motion.div>
          <h1 className="mt-4 max-w-4xl font-heading text-4xl font-black uppercase leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskedLines delay={0.3} lines={[project.name]} />
          </h1>
          {project.tagline && (
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }} className="mt-5 max-w-xl text-base text-zinc-300 sm:text-lg">
              {project.tagline}
            </motion.p>
          )}
        </div>
      </section>

      {/* Sobre / Objetivo / Desafio */}
      {(sections.about || sections.objective || sections.challenge) && (
        <section className="mx-auto grid max-w-[1600px] gap-12 px-5 py-20 sm:grid-cols-2 sm:px-8 sm:py-28 lg:grid-cols-3">
          {sections.about && (
            <Reveal data-testid="project-section-about">
              <Kicker>{t.project.about}</Kicker>
              <Md text={sections.about} className="mt-5" />
            </Reveal>
          )}
          {sections.objective && (
            <Reveal delay={0.1} data-testid="project-section-objective">
              <Kicker>{t.project.objective}</Kicker>
              <Md text={sections.objective} className="mt-5" />
            </Reveal>
          )}
          {sections.challenge && (
            <Reveal delay={0.2} data-testid="project-section-challenge">
              <Kicker>{t.project.challenge}</Kicker>
              <Md text={sections.challenge} className="mt-5" />
            </Reveal>
          )}
        </section>
      )}

      {/* O que fizemos */}
      {hasWhat && (
        <section className="border-t border-zinc-900" data-testid="project-section-services">
          <div className="mx-auto grid max-w-[1600px] gap-12 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-2">
            <Reveal>
              <Kicker>{t.project.whatWeDid}</Kicker>
              {sections.whatWeDid && <Md text={sections.whatWeDid} className="mt-5" />}
              {sections.process && <Md text={sections.process} className="mt-5" />}
            </Reveal>
            {project.services.length > 0 && (
              <Reveal delay={0.1}>
                <ul className="mt-2 space-y-3 lg:mt-10">
                  {project.services.map((s) => (
                    <li key={s[lang] || s.pt} className="flex items-center gap-3 border-b border-zinc-900 pb-3 text-lg text-zinc-200">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                      {s[lang] || s.pt}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* Galeria (ordem definida no admin) */}
      {project.media.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-5 pb-20 sm:px-8 sm:pb-28" data-testid="project-section-gallery">
          <Reveal className="mb-10">
            <Kicker>{t.project.gallery}</Kicker>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-6 md:gap-6">
            {project.media.map((m, i) =>
              m.media_type === 'photo' ? (
                <Reveal key={m.full} delay={(i % 3) * 0.08} className={SPANS[i % SPANS.length]}>
                  <button
                    onClick={() => setLightbox(photos.findIndex((ph) => ph.full === m.full))}
                    data-testid={`project-photo-${i}`}
                    data-cursor={t.cursor.view}
                    className={`group block w-full overflow-hidden bg-zinc-950 ${RATIOS[i % RATIOS.length]}`}
                  >
                    <img src={m.thumb || m.full} alt={m.alt?.[lang] || project.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  </button>
                </Reveal>
              ) : (
                <Reveal key={m.full} className="md:col-span-6" data-testid={`project-video-${i}`}>
                  <video controls playsInline preload="metadata" poster={m.poster || undefined} className="aspect-video w-full bg-zinc-950">
                    <source src={m.full} type="video/mp4" />
                  </video>
                </Reveal>
              )
            )}
          </div>
        </section>
      )}

      {/* Resultado — só com dados reais */}
      {hasResult && (
        <section className="border-t border-zinc-900" data-testid="project-section-results">
          <div className="mx-auto max-w-[1600px] px-5 py-20 sm:px-8 sm:py-28">
            <Reveal className="mb-8">
              <Kicker>{t.project.results}</Kicker>
            </Reveal>
            {sections.result && (
              <Reveal>
                <Md text={sections.result} className="max-w-2xl" />
              </Reveal>
            )}
            {project.metrics.length > 0 && (
              <div className="mt-10 grid grid-cols-2 gap-px bg-zinc-900 lg:grid-cols-4">
                {project.metrics.map((r, i) => (
                  <Reveal key={i} delay={i * 0.08} className="bg-black">
                    <div className="p-8 sm:p-10">
                      <p className="font-heading text-4xl font-black tracking-tight sm:text-5xl">{r.value}</p>
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{r.label?.[lang] || r.label?.pt}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Navegação */}
      <section className="border-t border-zinc-900">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-6 px-5 py-16 sm:px-8 sm:py-20">
          {nextInfo && (
            <Link to={projectUrl(nextInfo.slug, lang)} data-testid="project-next" data-cursor={t.cursor.open} className="group relative block overflow-hidden">
              <div className="relative flex min-h-[40vh] flex-col justify-end overflow-hidden p-8 sm:p-14">
                {nextInfo.cover && (
                  <img src={nextInfo.cover} alt={nextInfo.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-35 transition-all duration-700 group-hover:scale-105 group-hover:opacity-50" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="relative z-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{t.project.next}</p>
                  <p className="mt-3 flex items-center gap-4 font-heading text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
                    {nextInfo.name}
                    <ArrowRight className="h-8 w-8 shrink-0 transition-transform duration-500 group-hover:translate-x-2" />
                  </p>
                </div>
              </div>
            </Link>
          )}
          <Link to={`/${lang}/portfolio`} data-testid="project-back" className="inline-flex w-fit items-center gap-2 border border-zinc-800 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:border-fl-yellow hover:text-fl-yellow">
            <ArrowLeft className="h-4 w-4" /> {t.project.back}
          </Link>
        </div>
      </section>

      <AnimatePresence>
        {lightbox !== null && lightbox >= 0 && (
          <Lightbox photos={photos} index={lightbox} onClose={() => setLightbox(null)} onNav={(d) => setLightbox((c) => (c + d + photos.length) % photos.length)} closeLabel="Fechar" />
        )}
      </AnimatePresence>
    </div>
  );
}
